import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Select, ButtonGroup, Flex, Column, Spacer } from '@oliasoft-open-source/react-ui-library';
import { simulationChartType } from 'src/enums/compare-estimates';
import { probabilities, timeCostView } from 'src/enums/simulations';
import { CombinedChart } from 'views/compare-estimates/combined-result/combined-chart';
import { CombinedTable } from 'views/compare-estimates/combined-result/combined-table';
import translations from 'src/internationalisation/translation-map.json';
import { withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import { itemFields } from 'src/enums/time-tracker';

const CombinedResult = ({ estimates, estimateSelected }) => {
  const { t } = useTranslation();
  const [chartType, setChartType] = useState(simulationChartType.OPERATION_VS_DEPTH);
  const [probabilityTypes, setProbabilityTypes] = useState([probabilities.P10]);
  const [showChart, setShowChart] = useState(true);
  const [timeCostType, setTimeCostType] = useState(timeCostView.TIME);

  const controlsPortalId = `combined-chart-controls-${chartType}`;

  const handleSelection = (key) => {
    if (key === 0) {
      setShowChart(true);
    } else {
      setShowChart(false);
    }
  };

  const chartTypeOptions = [
    {
      label: t(
        timeCostType === timeCostView.TIME
          ? translations.simulations_timeDepthPlot
          : translations.simulations_costDepthPlot,
      ),
      value: simulationChartType.OPERATION_VS_DEPTH,
    },
    {
      label: t(translations.simulations_totalTimeAndCost),
      value: simulationChartType.SIMULATION_TOTAL_TIME_AND_COST,
    },
    {
      label: t(translations.simulations_cumulativeProbability),
      value: simulationChartType.CUMULATIVE_PROBABILITY,
    },
    {
      label: t(translations.costSetup_totalCost),
      value: simulationChartType.TOTAL_COST,
    },
  ];

  const probabilityOptions = Object.keys(probabilities).map((key) => ({
    label: key,
    value: probabilities[key],
  }));
  if (chartType === simulationChartType.OPERATION_VS_DEPTH && timeCostType === timeCostView.TIME) {
    probabilityOptions.push({
      label: t(translations.actual),
      value: itemFields.ACTUAL,
    });
  }

  return (
    <>
      <Flex gap justifyContent="space-between">
        <Column>
          <Flex alignItems="center" justifyContent="space-between" gap>
            <Flex gap>
              <Select
                testId="comparisons-select-chart-type"
                name="chart-type"
                width="auto"
                options={chartTypeOptions}
                small
                onChange={(e) => {
                  const { value } = e.target;
                  const probabilityTypesNoActual = probabilityTypes.filter((pT) => pT !== itemFields.ACTUAL);
                  setProbabilityTypes(probabilityTypesNoActual.length ? probabilityTypesNoActual : [probabilities.P50]);
                  if (value === simulationChartType.OPERATION_VS_DEPTH) {
                    setShowChart(true);
                  }
                  setChartType(value);
                }}
                value={chartType}
              />
              {chartType !== simulationChartType.OPERATION_VS_DEPTH && (
                <ButtonGroup
                  testId="comparisons-button-group-view-mode"
                  items={[
                    {
                      key: 0,
                      icon: 'chart',
                      disabled: showChart,
                      testId: 'chart',
                    },
                    {
                      key: 1,
                      icon: 'table',
                      disabled: !showChart,
                      testId: 'table',
                    },
                  ]}
                  small
                  onSelected={handleSelection}
                  value={showChart ? 0 : 1}
                />
              )}
              {(chartType === simulationChartType.OPERATION_VS_DEPTH ||
                chartType === simulationChartType.SIMULATION_TOTAL_TIME_AND_COST ||
                chartType === simulationChartType.TOTAL_COST) &&
                showChart && (
                  <Select
                    testId="comparisons-select-probability"
                    name="probability"
                    width={chartType === simulationChartType.OPERATION_VS_DEPTH ? 140 : 'auto'}
                    options={probabilityOptions}
                    small
                    onChange={(e) => {
                      const values =
                        chartType === simulationChartType.OPERATION_VS_DEPTH
                          ? e.target.value.map((val) => val.value)
                          : [e.target.value];
                      setProbabilityTypes(values);
                    }}
                    value={
                      chartType === simulationChartType.OPERATION_VS_DEPTH ? probabilityTypes : probabilityTypes[0]
                    }
                    multi={chartType === simulationChartType.OPERATION_VS_DEPTH}
                  />
                )}
              {chartType !== simulationChartType.TOTAL_COST &&
                chartType !== simulationChartType.SIMULATION_TOTAL_TIME_AND_COST && (
                  <ButtonGroup
                    testId="comparisons-button-group-time-cost-type"
                    items={[
                      {
                        key: timeCostView.TIME,
                        label: t(translations.time),
                        testId: 'time',
                      },
                      {
                        key: timeCostView.COST,
                        label: t(translations.cost),
                        testId: 'cost',
                      },
                    ]}
                    small
                    onSelected={(key) => {
                      const probabilityTypesNoActual = probabilityTypes.filter((pT) => pT !== itemFields.ACTUAL);
                      setProbabilityTypes(
                        probabilityTypesNoActual.length ? probabilityTypesNoActual : [probabilities.P10],
                      );
                      setTimeCostType(key);
                    }}
                    value={timeCostType}
                  />
                )}
            </Flex>
            <div id={controlsPortalId} />
          </Flex>
          <Spacer />

          {showChart ? (
            <CombinedChart
              key={`${chartType}`}
              estimates={estimates}
              chartType={chartType}
              probabilityTypes={probabilityTypes}
              timeCostType={timeCostType}
              controlsPortalId={controlsPortalId}
            />
          ) : (
            chartType !== simulationChartType.OPERATION_VS_DEPTH && (
              <CombinedTable
                estimates={estimates}
                estimateSelected={estimateSelected}
                chartType={chartType}
                timeCostType={timeCostType}
              />
            )
          )}
        </Column>
      </Flex>
    </>
  );
};

export default withErrorBoundary(CombinedResult);
